import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Space, InputNumber, Button, message, Input, Table, Tag } from 'antd';

type Props = {
  step?: any;
  data?: any;
  metrics?: any;
  onChange?: (k: string, v: any) => void;
  onSubmit?: () => Promise<void> | void;
  instructionCodeFromParent?: string;
  canOperate?: boolean;
  taskId?: number | null;
  stageNo?: number;
};

const RelabelStepRenderer: React.FC<Props> = ({ step, metrics, onChange, onSubmit, canOperate }) => {
  const [count, setCount] = useState<number>(Number(metrics?.label_count || 0) || 0);
  const saveTimer = useRef<any>(null);
  // 读取后端下发的基准数据（就绪 baselines），如 BARCODE_MAPPING
  const baselines: Array<any> = useMemo(() => {
    try {
      const arr = (step?.data?.baselines || []) as any[];
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  }, [step]);
  const hasMapping = baselines.some(b => String(b?.code || '').toUpperCase() === 'BARCODE_MAPPING');
  // 简易“旧→新条码映射”临时录入与打印（就绪基线存在与否均可使用，存在时仅作为提示与对照）
  const [oldCode, setOldCode] = useState<string>('');
  const [newCode, setNewCode] = useState<string>('');
  const [pairs, setPairs] = useState<Array<{ old: string; neu: string }>>([]);

  useEffect(() => {
    if (!onChange) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try { onChange('label_count', Number(count || 0)); } catch {}
    }, 300);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [count, onChange]);

  return (
    <div style={{ width: '100%' }}>
      <Card size="small" title="贴换标">
        <Space align="center">
          <span>换标总件数</span>
          <InputNumber disabled={!canOperate} min={0} value={count} onChange={(v)=> setCount(Number(v || 0))} />
          <Button type="primary" disabled={!canOperate} onClick={async () => {
            try {
              if (onChange) onChange('label_count', Number(count || 0));
              if (onSubmit) await onSubmit();
            } catch (e:any) {
              message.error(e?.message || '提交失败');
            }
          }}>提交</Button>
        </Space>
      </Card>
      <div style={{ height: 8 }} />
      <Card size="small" title="旧→新条码映射（扫描/录入）" extra={hasMapping ? <Tag color="green">存在模板基线</Tag> : <Tag>无模板基线</Tag>}>
        <Space wrap>
          <Input
            style={{ width: 220 }}
            placeholder="扫描旧条码"
            value={oldCode}
            onChange={(e)=> setOldCode((e.target.value || '').trim())}
            onPressEnter={()=> {
              const v = (oldCode || '').trim();
              if (!v) return;
              // 焦点跳到新条码
              try { (document.getElementById('new-barcode-input') as HTMLInputElement)?.focus(); } catch {}
            }}
            disabled={!canOperate}
          />
          <Input
            id="new-barcode-input"
            style={{ width: 220 }}
            placeholder="扫描/输入新条码"
            value={newCode}
            onChange={(e)=> setNewCode((e.target.value || '').trim())}
            onPressEnter={()=> {
              const o = (oldCode || '').trim();
              const n = (newCode || '').trim();
              if (!o || !n) { message.warning('请先扫描旧条码并输入新条码'); return; }
              setPairs(prev => {
                const next = prev.filter(p => p.old !== o);
                next.push({ old: o.toUpperCase(), neu: n.toUpperCase() });
                return next;
              });
              setOldCode('');
              setNewCode('');
            }}
            disabled={!canOperate}
          />
          <Button disabled={!canOperate} onClick={() => {
            const o = (oldCode || '').trim();
            const n = (newCode || '').trim();
            if (!o || !n) { message.warning('请先扫描旧条码并输入新条码'); return; }
            setPairs(prev => {
              const next = prev.filter(p => p.old !== o);
              next.push({ old: o.toUpperCase(), neu: n.toUpperCase() });
              return next;
            });
            setOldCode('');
            setNewCode('');
          }}>加入映射</Button>
          <Button type="primary" ghost onClick={() => {
            if (pairs.length === 0) { message.warning('无可打印的映射'); return; }
            const w = window.open('', '_blank');
            if (!w) { message.error('弹窗被拦截'); return; }
            const html = `
              <html><head><title>打印-条码映射</title></head>
              <body>
                <div style="font-weight:700;margin:8px 0;">条码映射清单（${pairs.length}）</div>
                <table border="1" cellspacing="0" cellpadding="6">
                  <thead><tr><th>旧条码</th><th>新条码</th></tr></thead>
                  <tbody>
                    ${pairs.map(p => `<tr><td>${p.old}</td><td>${p.neu}</td></tr>`).join('')}
                  </tbody>
                </table>
                <script>window.onload = function(){ window.print(); window.onafterprint = function(){ window.close(); }; };</script>
              </body></html>
            `;
            w.document.write(html);
            w.document.close();
          }}>打印映射清单</Button>
        </Space>
        <div style={{ marginTop: 12 }}>
          <Table
            size="small"
            rowKey={(r:any, i?:number)=> String(i ?? 0)}
            dataSource={pairs}
            pagination={false}
            columns={[
              { title: '旧条码', dataIndex: 'old' },
              { title: '新条码', dataIndex: 'neu' },
              { title: '操作', key: 'ops', width: 120, render: (_:any, row:any, idx:number) => (
                <Space>
                  <Button size="small" danger onClick={() => setPairs(prev => prev.filter((_, i)=> i !== idx))} disabled={!canOperate}>删除</Button>
                </Space>
              )},
            ] as any}
          />
        </div>
      </Card>
    </div>
  );
};

export default RelabelStepRenderer;


